import React, { useState, useEffect, useRef } from "react";

import "./ChatSettingsSelect.less";
import IntlMessages from "../../../../../../util/IntlMessages";
import { Button } from "../../../../../BP2Components";
import { ChatSettingsState } from "../../../../../../appRedux/reduxInterfaces/ReducersInterfaces";

export interface ChatOption {
   value: string | number;
   label: string;
}

export interface ChatSettingsSelectProps {
   options: ChatOption[];
   selectedUsers: ChatOption[];
   setSelectedUsers: (users: ChatOption[]) => void;
   handleAdd: () => void;
   chatSettings: ChatSettingsState;
   hideCreateButton?: boolean;
}

const ChatSettingsSelect: React.FC<ChatSettingsSelectProps> = ({
   options,
   selectedUsers,
   setSelectedUsers,
   handleAdd,
   chatSettings,
   hideCreateButton = false,
}) => {
   const [searchBoxClicked, setSearchBoxClicked] = useState(false);
   const [filteredOptions, setFilteredOptions] = useState(options);

   const [userInput, setUserInput] = useState<string>();

   const containerRef = useRef<HTMLDivElement>(null);
   const inputRef = useRef<HTMLInputElement>(null);

   // clearing user input after clicking outside or in the input
   useEffect(() => {
      setUserInput("");
      setFilteredOptions(options);
   }, [searchBoxClicked]);

   // all-active request is slower then firebase. And we are fetching it when opening chat.
   // Drilling through all components to this one to pass "loading" will be nightmare. Instead of we are rerendering component (by adding all active in state)
   // options rerender in parent component when chatData changes
   useEffect(() => {
      if (!userInput && selectedUsers.length === 0) {
         setFilteredOptions(options);
      }
   }, [options, userInput, selectedUsers]);

   //  filter options based on search input
   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase();

      setUserInput(searchValue);

      const filtered = options.filter(
         (option) =>
            option.label.toLowerCase().includes(searchValue) &&
            // if options are selected already, filter it out
            !selectedUsers.some((selected) => selected.value === option.value),
      );

      setFilteredOptions(filtered);
   };

   const handleOptionClick = (option: ChatOption) => {
      //@ts-ignore
      setSelectedUsers((prevState) => [...prevState, option]);

      // removing clicked option from options to select
      setFilteredOptions((prevState) => prevState.filter((item) => item.value !== option.value));

      setTimeout(() => {
         inputRef.current?.focus();
      }, 0);
   };

   const handleRemoveSelectedOption = (option: ChatOption) => {
      // Remove the clicked option from selected options
      //@ts-ignore
      setSelectedUsers((prevState) => prevState.filter((item) => item.value !== option.value));

      // If no user input, add the option back to the filtered options
      if (!userInput) {
         return setFilteredOptions((prevState) => [...prevState, option]);
      }

      // Filter options that match the user input and are not already in filteredOptions
      const filtered = options.filter(
         (opt) =>
            opt.label.toLowerCase().includes(userInput) && // if contain user input
            (!selectedUsers.some((selectedOption) => selectedOption.value === opt.value) || // is not already choosen
               opt.value === option.value), // EXCEPTION: Add the option you just removed if it is in user Input
      );

      setFilteredOptions(filtered);
   };

   const handleAddButton = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (handleAdd) {
         handleAdd();
      }
   };

   // Handle click outside of dropdown to close
   const handleClickOutside = (event: MouseEvent) => {
      if (
         containerRef.current &&
         //@ts-ignore
         !containerRef.current.contains(event.target)
      ) {
         setSearchBoxClicked(false);
      }
   };

   useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, []);

   return (
      <div className="chat-select-main-container" ref={containerRef}>
         <div
            className="chat-select-main-container__search-box"
            onClick={() => setSearchBoxClicked(true)}
         >
            {searchBoxClicked ? (
               <input
                  className="search-box-input"
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={handleInputChange}
                  autoFocus={searchBoxClicked}
               />
            ) : (
               <div className="placeholder-container">
                  <div className="left-side-content">
                     {selectedUsers?.length > 0 ? (
                        <div className="left-side-content__selected-users">
                           {" "}
                           {selectedUsers?.map((option) => (
                              <div
                                 onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveSelectedOption(option);
                                 }}
                                 className="selected-option-container"
                              >
                                 <div key={option.value} className="selected-option">
                                    {option.label}
                                 </div>
                                 <span className="material-symbols-outlined remove-user">
                                    delete_forever
                                 </span>
                              </div>
                           ))}
                        </div>
                     ) : (
                        <>
                           <span className="material-symbols-outlined lean">search</span>
                           <IntlMessages id="chat.searchUsers" />
                        </>
                     )}
                  </div>
               </div>
            )}
            {!hideCreateButton && (
               <div className="right-side-content">
                  <Button
                     onClick={(e) => {
                        e.stopPropagation();
                        handleAddButton(e);
                        setSearchBoxClicked(false);
                     }}
                     className="right-side-content__add-button"
                  >
                     <IntlMessages id="chat.add" />
                  </Button>
               </div>
            )}
         </div>
         {searchBoxClicked && (
            <div className="chat-select-main-container__dropdown">
               {selectedUsers?.map((option) => (
                  <div
                     onClick={() => handleRemoveSelectedOption(option)}
                     className="selected-option-container"
                  >
                     <div key={option.value} className="selected-option">
                        {option.label}
                     </div>
                     <span className="material-symbols-outlined remove-user">delete_forever</span>
                  </div>
               ))}
               {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                     <div
                        key={option.value}
                        className="dropdown-option"
                        onClick={() => {
                           handleOptionClick(option);
                           setUserInput("");
                        }}
                     >
                        {option.label}
                     </div>
                  ))
               ) : (
                  <div className="dropdown-no-options" style={{ marginTop: "10px" }}>
                     <IntlMessages id="chat.selectNoMoreUserResults" />
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default ChatSettingsSelect;
